import Head from 'next/head'
import { useRouter } from 'next/router'
import { Link as ReachLink } from '@reach/router'
import {
  Breadcrumbs,
  Container,
  Link as ChakraLink,
  LinkProps,
  NavTab,
  NavTabList,
  NavTabs,
  PageHeader,
  PageTitle,
  Skeleton,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  useDisclosure
} from '~/design_system'
import { links } from '~/common/links'
import { FiltersSidebar } from '~/components/v2/FiltersSidebar'
import { ActionBar, ResultsLayout } from '~/components/v2/ResultsLayout'
import { FiltersProvider } from '~/context/filters'
import { useVendorVisible } from '~/api/work_order'
import { useIsWithinReachRouter } from '~/common/reachRouterUtils'
import { NamedQueryFilter } from '~/custom_reports/types'
import { lazyMsg, Msg, useMsg } from '~/common/localizationUtils'
import { SadStates } from '~/components/SadStates'
import { Filter } from '~/components/v2/FiltersSidebar/types'

export const DEFAULT_JOB_STATUS = '4'

const INVOICED = '7'
const PAID_CLOSED = '9'
const PAID_PARTIALLY = '10'
export const DEFAULT_INVOICE_STATUS = [INVOICED, PAID_CLOSED, PAID_PARTIALLY]

export type Fields = {
  id: string
  value: string
}

export type DashboardQueryOrderBy = {
  id: string
  direction: 'asc' | 'desc'
}

export type DashboardQueryParams = {
  filters: NamedQueryFilter[]
}

export type DashboardQueryItem = {
  [key: string]: string | number
}

type FilterConfig = {
  id: string
  allowedFor: Array<string>
  type: string
  config?: { maxCheckableCount: number }
  optionsFromApi?: {
    transform: string
    pageSize: number
    searchParamKey: string
    url: string
  }
  title: React.ReactNode
}

type DashboardPageContainerProps = {
  children: React.ReactNode
  filtersConfig?: Filter[] | FilterConfig[]
  enableFilters?: boolean
  savedFilterNamespace?: string
}

export const DashboardPageContainer = ({
  children,
  filtersConfig,
  enableFilters = true,
  savedFilterNamespace
}: DashboardPageContainerProps) =>
  enableFilters ? (
    <FiltersProvider filters={filtersConfig} applyDebounceMs={1000} keepNullValues>
      <PageWrapper enableFilters={enableFilters} savedFilterNamespace={savedFilterNamespace}>
        {children}
      </PageWrapper>
    </FiltersProvider>
  ) : (
    <PageWrapper enableFilters={enableFilters}>{children}</PageWrapper>
  )

type PageWrapperProps = {
  children: React.ReactNode
  enableFilters: boolean
  savedFilterNamespace?: string
}

const PageWrapper = ({ children, enableFilters, savedFilterNamespace }: PageWrapperProps) => {
  const { isOpen, onClose, onToggle } = useDisclosure()
  return (
    <Container>
      <ResultsLayout allowDeprecatedUseOutsideOfListLayout>
        {enableFilters && (
          <FiltersSidebar isOpen={isOpen} onClose={onClose} savedFilterNamespace={savedFilterNamespace} />
        )}
        <ResultsLayout.Main spacing={0}>
          {enableFilters && (
            <ActionBar display={{ lg: 'none' }} mb={6} hideCard>
              <ActionBar.ToggleFiltersButton onClick={onToggle} />
            </ActionBar>
          )}
          <ResultsLayout.MainContent>{children}</ResultsLayout.MainContent>
        </ResultsLayout.Main>
      </ResultsLayout>
    </Container>
  )
}

export const DashboardPageHeader = ({ title }: { title: string }) => {
  const msg = useMsg()

  return (
    <>
      <Head>
        <title>{msg('common.workday_dashboard_title')}</title>
      </Head>
      <PageHeader pb="0">
        <Breadcrumbs links={[links.home, { name: msg('nav.dashboard'), href: '/dashboards' }, { name: title }]} />
        <PageTitle>{msg('nav.dashboard')}</PageTitle>
        <DashboardPageTabs />
      </PageHeader>
    </>
  )
}
DashboardPageHeader.displayName = 'DashboardPageHeader'

const dashboardPages = new Map([
  [lazyMsg('common.ui.summary'), '/dashboards/summary'],
  [lazyMsg('common.ui.spend'), '/dashboards/spend'],
  [lazyMsg('term.headcount'), '/dashboards/headcount'],
  [lazyMsg('term.job', { count: 999 }), '/dashboards/jobs'],
  [lazyMsg('term.workorder', { count: 999 }), '/dashboards/work-orders'],
  [lazyMsg('term.vendor', { count: 999 }), '/dashboards/vendors']
])

const DashboardPageTabs = () => {
  const { pathname } = useRouter()
  const { isVendorVisible, isLoading } = useVendorVisible()
  const permittedDashboardPages = new Map(
    [...dashboardPages].filter(f => {
      if (!isVendorVisible) return f[1] !== '/dashboards/vendors'
      return f
    })
  )
  if (isLoading) {
    return (
      <Stack direction="row" spacing={4} mt={4}>
        <Skeleton height="40px" width="100px" />
        <Skeleton height="40px" width="100px" />
        <Skeleton height="40px" width="100px" />
        <Skeleton height="40px" width="100px" />
        <Skeleton height="40px" width="100px" />
      </Stack>
    )
  }

  return (
    <NavTabs defaultIndex={[...permittedDashboardPages].findIndex(t => t[1] === pathname)} fontSize="md">
      <NavTabList>
        {[...permittedDashboardPages].map(tab => (
          <NavTab href={tab[1]} key={tab[1]}>
            {tab[0]}
          </NavTab>
        ))}
      </NavTabList>
    </NavTabs>
  )
}

export const useWidgetReportUrl = (): { url: string } => {
  const url = '/reporting/new'
  return { url }
}

type FloatingReportLinkProps = {
  reportKey: string
  title: string
  filter?: string
  children: React.ReactNode
} & LinkProps

export const FloatingReportLink = ({ reportKey, title, filter, children, ...props }: FloatingReportLinkProps) => {
  const { url: widgetReportUrl } = useWidgetReportUrl()
  const url = `${widgetReportUrl}?&key=${reportKey}`
  const isReachRouter = useIsWithinReachRouter()
  return (
    <ChakraLink
      {...(isReachRouter && { as: ReachLink })}
      variant="unstyled"
      to={url}
      href={url}
      target="_blank"
      state={{ reportKey, title }}
      _hover={{ textDecoration: 'unset' }}
      {...props}>
      {children}
    </ChakraLink>
  )
}

type StatCardProps = {
  id: string
  currentData: string | number
  labelKey: string
  helpText?: string
  isLoading: boolean
  isFetching: boolean
}

export const StatCard = ({ id, currentData, labelKey, helpText, isLoading, isFetching }: StatCardProps) => {
  // no help text means it's a summary card
  const isSummary = !helpText
  return (
    <SadStates states={[{ when: isLoading || isFetching, render: () => <StatChartSkeleton isSummary={isSummary} /> }]}>
      <Stat data-testid={`${id}_card`}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <StatNumber fontWeight="normal">{currentData ?? 0}</StatNumber>
          <StatLabel fontSize="sm" fontWeight={500}>
            <Msg id={labelKey} />
          </StatLabel>
        </Stack>
        {!isSummary && <StatLabel>{helpText}</StatLabel>}
      </Stat>
    </SadStates>
  )
}

export const StatChartSkeleton = ({ isSummary }: { isSummary: boolean }) => (
  <Stack spacing={3}>
    <Stack direction="row" spacing={3}>
      <Skeleton height="32px" width="30px" />
      <Skeleton height="16px" width="60px" alignSelf="flex-end" />
    </Stack>
    {!isSummary && <Skeleton height="12px" width="100px" />}
  </Stack>
)

export const AverageTimeStatChartSkeleton = () => (
  <Stack spacing={3}>
    <Skeleton height="16px" width="100px" />
    <Skeleton height="12px" width="120px" />
    <Skeleton height="24px" width="60px" />
  </Stack>
)
